/**
 * Regras puras da Programação do cliente, sem React, para o teste alcançar sem montar
 * tela. Valem para o portal e para o ajuste da equipe no Hub.
 *
 * A carga tem identidade: a que veio do servidor leva o `uuid`, a nova leva `uuid`
 * nulo. É pela identidade que se conta a carga extra e a redução, como no servidor:
 * a nova é extra, a que saiu é redução, e o horário mudado na mesma carga é só
 * reprogramação de horário (Samuel, 03/11/2026).
 */
import dayjs from 'dayjs'

export const MAX_CARGAS = 40
/** Até 10 horas entre uma carga e a próxima. */
export const INTERVALO_MAXIMO = 600
export const INTERVALO_PADRAO = 60
export const INICIO_PADRAO = '07:00'
export const DIAS_NA_FAIXA = 7

export const horarioValido = (h: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(h)

/** Soma minutos a um HH:MM. Passar da meia-noite para em 23:59: a programação tem
 *  uma data só, e virar o dia escondido no horário enganaria o cliente. */
export function somarMinutos(hhmm: string, minutos: number): string {
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
  const total = Math.min(h * 60 + m + minutos, 23 * 60 + 59)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** Os horários gerados pelo primeiro horário e pelo intervalo. */
export function gerarHorarios(inicio: string, intervalo: number, quantidade: number): string[] {
  return Array.from({ length: quantidade }, (_, i) => somarMinutos(inicio, i * Math.max(0, intervalo)))
}

/** O intervalo que a grade existente sugere, para o ajuste em bloco continuar nele. */
export function intervaloSugerido(horarios: string[]): number {
  const ord = [...horarios].filter(horarioValido).sort()
  if (ord.length < 2) return INTERVALO_PADRAO
  const [h1, m1] = ord[0].split(':').map(Number)
  const [h2, m2] = ord[1].split(':').map(Number)
  const diff = h2 * 60 + m2 - (h1 * 60 + m1)
  return diff > 0 ? diff : INTERVALO_PADRAO
}

// ── As linhas do editor ──────────────────────────────────────────────────────

export type OrigemDaCarga = 'cliente' | 'equipe'

export interface LinhaDeCarga {
  /** Chave da linha na tela: o uuid da carga, ou uma chave nova para a que entra. */
  chave: string
  /** O uuid da carga que já existe; nulo na que entra. */
  uuid: string | null
  horario: string
  origem?: OrigemDaCarga
}

let contador = 0
export const novaChave = () => `nova-${Date.now().toString(36)}-${(contador += 1)}`

export function linhasDe(cargas: { uuid: string; horario: string | null; origem?: OrigemDaCarga }[]): LinhaDeCarga[] {
  return ordenarLinhas(cargas.map((c) => ({ chave: c.uuid, uuid: c.uuid, horario: c.horario ?? INICIO_PADRAO, origem: c.origem })))
}

export function linhasNovas(horarios: string[]): LinhaDeCarga[] {
  return horarios.map((h) => ({ chave: novaChave(), uuid: null, horario: h }))
}

/** Pelo horário, e a ordem de entrada desempata: é a ordem do número da carga. */
export function ordenarLinhas(linhas: LinhaDeCarga[]): LinhaDeCarga[] {
  return linhas
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (a.l.horario === b.l.horario ? a.i - b.i : a.l.horario < b.l.horario ? -1 : 1))
    .map((x) => x.l)
}

/** Muda a quantidade da programação nova sem mexer no que já está na grade: a carga
 *  nova entra depois da última, no intervalo, e a que sai é a do fim. */
export function redimensionar(linhas: LinhaDeCarga[], quantidade: number, inicio: string, intervalo: number): LinhaDeCarga[] {
  if (quantidade <= linhas.length) return linhas.slice(0, Math.max(0, quantidade))
  const out = [...linhas]
  while (out.length < quantidade) {
    const ultimo = out[out.length - 1]?.horario
    out.push({ chave: novaChave(), uuid: null, horario: ultimo ? somarMinutos(ultimo, Math.max(0, intervalo)) : inicio })
  }
  return out
}

/** A reprogramação "a partir da carga N": da carga N em diante, o primeiro horário e
 *  o intervalo novos, pela ordem do horário. As de antes ficam como estão. */
export function aplicarAPartirDe(linhas: LinhaDeCarga[], numero: number, inicio: string, intervalo: number): LinhaDeCarga[] {
  const ord = ordenarLinhas(linhas)
  const desde = Math.max(1, numero) - 1
  return ord.map((l, i) => (i < desde ? l : { ...l, horario: somarMinutos(inicio, (i - desde) * Math.max(0, intervalo)) }))
}

/** A próxima carga que entra pelo botão: depois da última, no intervalo da grade. */
export function proximoHorario(linhas: LinhaDeCarga[]): string {
  const ord = ordenarLinhas(linhas).map((l) => l.horario).filter(horarioValido)
  if (ord.length === 0) return INICIO_PADRAO
  return somarMinutos(ord[ord.length - 1], intervaloSugerido(ord))
}

export interface Classificacao {
  extras: number
  reducoes: number
  horariosAlterados: number
}

/** Carga extra, redução e horário mudado, pela identidade da carga. */
export function classificar(originais: LinhaDeCarga[], linhas: LinhaDeCarga[]): Classificacao {
  const antes = new Map(originais.filter((l) => l.uuid).map((l) => [l.uuid as string, l.horario]))
  const ficam = linhas.filter((l) => l.uuid && antes.has(l.uuid))
  return {
    extras: linhas.filter((l) => !l.uuid).length,
    reducoes: antes.size - ficam.length,
    horariosAlterados: ficam.filter((l) => antes.get(l.uuid as string) !== l.horario).length,
  }
}

export const semMudanca = (c: Classificacao) => c.extras === 0 && c.reducoes === 0 && c.horariosAlterados === 0

/** O corpo da reprogramação: a lista inteira, pela identidade. */
export function corpoDasCargas(linhas: LinhaDeCarga[]): { uuid: string | null; horario: string }[] {
  return ordenarLinhas(linhas).map((l) => ({ uuid: l.uuid, horario: l.horario }))
}

// ── A faixa de dias e os horários ────────────────────────────────────────────

export interface DiaDaFaixa {
  iso: string
  /** Hoje, Amanhã, ou o dia da semana. */
  rotulo: 'hoje' | 'amanha' | 'semana'
  diaMes: string
}

export function proximosDias(hoje: string, n = DIAS_NA_FAIXA): DiaDaFaixa[] {
  const base = dayjs(hoje)
  return Array.from({ length: n }, (_, i) => {
    const d = base.add(i, 'day')
    return { iso: d.format('YYYY-MM-DD'), rotulo: i === 0 ? 'hoje' : i === 1 ? 'amanha' : 'semana', diaMes: d.format('DD/MM') }
  })
}

export function faixaDeHorarios(lista: (string | null)[]): { primeiro: string; ultimo: string } | null {
  const validos = lista.filter((h): h is string => Boolean(h)).sort()
  if (validos.length === 0) return null
  return { primeiro: validos[0], ultimo: validos[validos.length - 1] }
}

/**
 * O que muda entre duas listas de horários, para o antes e agora do aviso da equipe.
 * Compara as listas ordenadas: o horário que some de um lado e aparece no outro é
 * destaque.
 */
export function diferenca(antes: string[], depois: string[]): { saiu: Set<string>; entrou: Set<string> } {
  const restoAntes = [...antes]
  const entrou = new Set<string>()
  for (const h of depois) {
    const i = restoAntes.indexOf(h)
    if (i >= 0) restoAntes.splice(i, 1)
    else entrou.add(h)
  }
  return { saiu: new Set(restoAntes), entrou }
}
